import React, { useState, useEffect } from "react";
import { PageLayout } from "../ui-components/PageLayout";
import { PieChartWrapper } from "../ui-components/Charts/PieChartWrapper";
import { BarChartWrapper } from "../ui-components/Charts/BarChartWrapper";
import {
  ToggleButton,
  ToggleButtonGroup,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
  Drawer,
  IconButton,
  Fab,
  Badge,
  Tab,
  Tabs,
  Tooltip,
  Checkbox,
  ListItemText,
  Alert,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import { LineChartWrapper } from "../ui-components/Charts/LineChartWrapper";
import AllVariablesTrendChart from "../ui-components/Charts/AllVariablesTrendChart";
import AggregateTrendChart from "../ui-components/Charts/AggregateTrendChart";
import { RootState } from "../store/reducers";
import { useDispatch, useSelector } from "react-redux";
import { getCategoryDataSummary } from "../store/modules/analytics/analyticsActions";
import { fetchTransactionYears } from "../store/modules/transactionYear/transactionYearActions";
import { AppDispatch } from "../store/store";
import LoadingDialog from "../ui-components/Dialogs/LoadingDialog";
import {
  NO_YEARS_SELECTED,
  YearFilterSelect,
} from "../ui-components/YearFilterSelect";
import LeadToClosedChart from "../ui-components/Charts/LeadToClosedChart";
import {
  analyticsRequestYearIds,
  usePipelineEnabled,
  usePipelineFolder,
} from "../hooks/usePipeline";
import { TransactionTypeFilterSelect } from "../ui-components/TransactionTypeFilterSelect";
import { useQuery } from "@tanstack/react-query";
import { AggregationTypesEnum } from "../types/types";
import { CoAgentFilterSelect } from "../ui-components/CoAgentFilterSelect";
import { usePageCoAgentFilter } from "../hooks/usePageCoAgentFilter";

const categoryColumns = [
  "total",
  // "average",
  "type",
  "source",
  "reason",
  // "area",
  "coAgent",
  "specialGroups",
  "specialTags",
  "zipCode",
  "county",
  "customArea",
  "vendors",
  "leadToClosed",
] as const;

const categoryLabels = {
  total: "Total S&B",
  // average: "Average Price S&B",
  type: "Type",
  source: "Source",
  reason: "Reason",
  // area: "Area",
  coAgent: "Team Member/Co-Agent",
  specialGroups: "Special Groups",
  specialTags: "Special Tags",
  zipCode: "Zip Code",
  county: "County",
  customArea: "Custom Area",
  vendors: "Vendors",
  leadToClosed: "Lead to Closed",
} as const satisfies Record<(typeof categoryColumns)[number], string>;

const Reports = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { hiddenFields } = useSelector(
    (state: RootState) => state.hiddenFields,
  );
  const { selectedCoAgentIds } = useSelector(
    (state: RootState) => state.coAgentFilter,
  );
  const { data: userData } = useSelector(
    (state: RootState) => state.commonData.userData,
  );

  const coAgents = userData?.coAgents || [];

  // Page-specific co-agent filter
  const [effectiveCoAgentIds, setPageCoAgentIds, isGlobalFilterActive] =
    usePageCoAgentFilter("analyticsReports", selectedCoAgentIds);

  const pipelineEnabled = usePipelineEnabled();
  const { data: pipelineFolder } = usePipelineFolder(pipelineEnabled);
  const pipelineYearId = pipelineEnabled ? pipelineFolder?.id : undefined;

  const visibleCategoryColumns = [
    // "total",
    // "average",
    ...categoryColumns.filter(
      (column) =>
        !hiddenFields.find((field) => field.name === column) &&
        // Lead to Closed is a Pipeline feature
        (column !== "leadToClosed" || pipelineEnabled),
    ),
  ] as const;
  const [selectedCategory, setSelectedCategory] = useState<
    (typeof categoryColumns)[number]
  >(categoryColumns[0] as (typeof categoryColumns)[number]);
  const { years } = useSelector((state: RootState) => state.transactionYear);
  const transactionTypes = useSelector(
    (state: RootState) => state.commonData.userData.data?.transactionTypes,
  );
  const [selectedYearIds, setSelectedYearIds] = useState<number[]>([]);
  // Lead to Closed only covers closed Transactions. A selection of only the
  // Pipeline must match nothing, not fall back to "all years" ([]).
  const leadToClosedYearIds = (() => {
    const ids = selectedYearIds.filter((id) => id !== pipelineYearId);
    return selectedYearIds.length > 0 && ids.length === 0
      ? [NO_YEARS_SELECTED]
      : ids;
  })();
  // "Under Contracts" adds a dot per period; it is a flag, not a year
  const [includeUnderContracts, setIncludeUnderContracts] = useState(false);
  // Real year folders picked (the Pipeline is shown as dots, never as a year)
  const realSelectedYearCount = selectedYearIds.filter(
    (id) => id !== pipelineYearId && id !== NO_YEARS_SELECTED,
  ).length;
  const [selectedTransactionTypeIds, setSelectedTransactionTypeIds] = useState<
    number[]
  >([]);

  useQuery({
    queryKey: ["fetchTransactionYears", selectedCoAgentIds],
    queryFn: () => {
      dispatch(fetchTransactionYears());
      return Promise.resolve(null);
    },
    refetchOnWindowFocus: false,
  });

  // What the pie and bar charts cover, e.g. "All Years except 2016 + Pipeline + Under Contracts"
  const selectedYearText = (() => {
    const yearFolders = years.filter((y) => y.id !== pipelineYearId);
    const checked = yearFolders.filter((y) => selectedYearIds.includes(y.id!));
    const unchecked = yearFolders.filter(
      (y) => !selectedYearIds.includes(y.id!),
    );
    const newestFirst = (list: typeof yearFolders) =>
      [...list]
        .sort((a, b) => (b.year ?? 0) - (a.year ?? 0))
        .map((y) => y.year)
        .join(", ");

    const parts: string[] = [];
    if (selectedYearIds.length === 0 || unchecked.length === 0) {
      parts.push("All Years");
    } else if (checked.length > 0 && unchecked.length < checked.length) {
      parts.push(`All Years except ${newestFirst(unchecked)}`);
    } else if (checked.length > 0) {
      parts.push(newestFirst(checked));
    }
    if (
      pipelineYearId !== undefined &&
      selectedYearIds.includes(pipelineYearId)
    ) {
      parts.push("Pipeline");
    }
    if (includeUnderContracts) parts.push("Under Contracts");
    return parts.length > 0 ? parts.join(" + ") : "None selected";
  })();

  const [selectedVariable, setSelectedVariable] = useState<{
    id: number;
    value: string;
    color: string;
  } | null>(null);
  const [mode, setMode] = useState<"count" | "amount" | "average">("count");
  const [timePeriod, setTimePeriod] = useState<"quarter" | "month" | "year">(
    "year",
  );

  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const { loading } = useSelector((state: RootState) => state.analytics);

  // Add state for chart expand triggers
  const [expandPieChart, setExpandPieChart] = useState(false);
  const [expandLineChart, setExpandLineChart] = useState(false);
  const [expandAllVariablesChart, setExpandAllVariablesChart] = useState(false);

  // Reset expand triggers after they're used
  useEffect(() => {
    if (expandLineChart) {
      setTimeout(() => setExpandLineChart(false), 100);
    }
  }, [expandLineChart]);

  useEffect(() => {
    if (expandAllVariablesChart) {
      setTimeout(() => setExpandAllVariablesChart(false), 100);
    }
  }, [expandAllVariablesChart]);

  useEffect(() => {
    if (expandPieChart) {
      setTimeout(() => setExpandPieChart(false), 100);
    }
  }, [expandPieChart]);

  const handleSliceClick = (
    variableId: number,
    value: string,
    color: string,
  ) => {
    setSelectedVariable({
      id: variableId,
      value: value,
      color: color,
    });
  };

  const handleYearsChange = (value: number[]) => {
    setSelectedYearIds(value);
    // A single year is shown by quarter (the Pipeline doesn't count as a year)
    if (
      value.filter((id) => id !== pipelineYearId && id !== NO_YEARS_SELECTED)
        .length === 1
    ) {
      setTimePeriod("quarter");
    }
  };

  const handleTabChange = (
    event: React.SyntheticEvent,
    newValue: (typeof categoryColumns)[number],
  ) => {
    setSelectedCategory(newValue);
    // if (newValue === "average") {
    //   setMode("amount");
    // }
    setSelectedVariable(null);
  };

  // Filter content component to reuse in both sidebar and drawer
  const FilterContent = (
    <Box sx={{ p: 3 }}>
      <Typography
        variant="h5"
        color="primary"
        component="h2"
        fontWeight="bold"
        gutterBottom
        sx={{ mb: 3 }}
      >
        Filters
      </Typography>

      <Box sx={{ mb: 4 }}>
        <YearFilterSelect
          years={years}
          selectedYearIds={selectedYearIds}
          onChange={handleYearsChange}
          label={pipelineYearId !== undefined ? "Years/Pipeline" : "Year"}
          pipelineYearId={pipelineYearId}
          underContractsChecked={includeUnderContracts}
          onUnderContractsChange={setIncludeUnderContracts}
          bulkActions
          size="medium"
          sx={{
            mb: 3,
            backgroundColor: "white",
            "& .MuiOutlinedInput-root": {
              "&:hover fieldset": {
                borderColor: "primary.main",
              },
            },
          }}
        />

        <FormControl fullWidth sx={{ mb: 3 }}>
          <TransactionTypeFilterSelect
            transactionTypes={transactionTypes || []}
            selectedTransactionTypeIds={selectedTransactionTypeIds}
            onChange={(value) => {
              setSelectedTransactionTypeIds(value);
            }}
            label="Transaction Type"
            sx={{
              backgroundColor: "white",
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": {
                  borderColor: "primary.main",
                },
              },
            }}
          />
        </FormControl>

        {/* Page-level Co-Agent Filter (hidden when global filter is active or coAgent category is hidden) */}
        {!isGlobalFilterActive &&
          !hiddenFields?.some((field) => field.name === "coAgent") && (
            <FormControl fullWidth sx={{ mb: 3 }}>
              <CoAgentFilterSelect
                coAgents={coAgents}
                selectedCoAgentIds={effectiveCoAgentIds}
                onChange={setPageCoAgentIds}
                label="Filter by Team Members"
                size="medium"
                sx={{
                  backgroundColor: "white",
                  "& .MuiOutlinedInput-root": {
                    "&:hover fieldset": {
                      borderColor: "primary.main",
                    },
                  },
                }}
              />
            </FormControl>
          )}

        {/* {selectedCategory !== "average" && ( */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, color: "#666" }}>
            Data Analyzed
          </Typography>
          <ToggleButtonGroup
            value={mode}
            exclusive
            onChange={(_, newMode) => newMode && setMode(newMode)}
            fullWidth
            size="small"
          >
            <ToggleButton value="count">Units</ToggleButton>
            <ToggleButton value="amount">Volume</ToggleButton>

            <ToggleButton value="average">Average Price (S&amp;B)</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        {/* )} */}
      </Box>

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 2, color: "#666" }}>
          Category
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {visibleCategoryColumns.map((category) => (
            <Box
              key={category}
              onClick={() => {
                setSelectedCategory(category);
                // if (category === "average") {
                //   setMode("amount");
                // }
                setSelectedVariable(null);
                if (isMobile) {
                  setMobileFiltersOpen(false);
                }
              }}
              sx={{
                p: 2,
                borderRadius: 2,
                cursor: "pointer",
                backgroundColor:
                  selectedCategory === category ? "primary.main" : "white",
                color:
                  selectedCategory === category
                    ? "primary.contrastText"
                    : "#222",
                border: "1px solid #e0e0e0",
                fontWeight: selectedCategory === category ? 600 : 400,
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor:
                    selectedCategory === category ? "primary.dark" : "#f5f5f5",
                },
              }}
            >
              {categoryLabels[category]}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );

  const categoryRequestYearIds = analyticsRequestYearIds(
    selectedYearIds,
    years,
    pipelineYearId,
    includeUnderContracts,
  );
  useQuery({
    queryKey: [
      "getCategoryDataSummary",
      selectedCategory,
      categoryRequestYearIds,
      selectedTransactionTypeIds,
      effectiveCoAgentIds,
    ],
    queryFn: () => {
      // Skip API call for aggregate tabs
      if (selectedCategory !== "total") {
        //&& selectedCategory !== "average"
        //
        dispatch(
          getCategoryDataSummary(
            categoryRequestYearIds,
            selectedCategory,
            selectedTransactionTypeIds,
            effectiveCoAgentIds,
          ),
        );
      }
      return Promise.resolve(null);
    },
    refetchOnWindowFocus: false,
    enabled:
      selectedCategory !== undefined &&
      selectedCategory !== "total" &&
      selectedCategory !== "leadToClosed",
    //selectedCategory !== "average",
  });

  return (
    <PageLayout>
      <LoadingDialog open={loading} message="Loading Data..." />

      {/* Desktop: Sticky Header Layout */}
      {!isMobile && (
        <>
          <Typography
            variant="h4"
            color="primary"
            component="h1"
            fontWeight="bold"
            gutterBottom
            sx={{ mb: 2, ml: 1 }}
          >
            Analytics Overview
          </Typography>

          {/* Sticky Filter Section */}
          <Box
            sx={{
              position: "sticky",
              top: 0, // Change back to 0 since we're now in the scrollable container
              zIndex: 1000,
              backgroundColor: "white",
              borderBottom: "1px solid #e0e0e0",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              pb: 1.5,
              mb: 2.5,
              pt: 1.5,
              px: 2,
              borderRadius: "0 0 12px 12px",
              mx: 1, // Add horizontal margin to match the title
            }}
          >
            {/* Filter Controls Row - single compact toolbar row */}
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 1.5,
                mb: 1.5,
              }}
            >
              <YearFilterSelect
                years={years}
                selectedYearIds={selectedYearIds}
                onChange={handleYearsChange}
                label={pipelineYearId !== undefined ? "Years/Pipeline" : "Year"}
                pipelineYearId={pipelineYearId}
                underContractsChecked={includeUnderContracts}
                onUnderContractsChange={setIncludeUnderContracts}
                bulkActions
                size="small"
                fullWidth={false}
                sx={{
                  minWidth: 150,
                  flex: "0 1 200px",
                  backgroundColor: "white",
                  "& .MuiOutlinedInput-root": {
                    "&:hover fieldset": {
                      borderColor: "primary.main",
                    },
                  },
                }}
              />

              <TransactionTypeFilterSelect
                transactionTypes={transactionTypes || []}
                selectedTransactionTypeIds={selectedTransactionTypeIds}
                onChange={(value) => {
                  setSelectedTransactionTypeIds(value);
                }}
                label="Transaction Type"
                size="small"
                fullWidth={false}
                sx={{
                  minWidth: 160,
                  flex: "0 1 210px",
                  backgroundColor: "white",
                  "& .MuiOutlinedInput-root": {
                    "&:hover fieldset": {
                      borderColor: "primary.main",
                    },
                  },
                }}
              />

              {/* Page-level Co-Agent Filter (hidden when global filter is active or coAgent category is hidden) */}
              {!isGlobalFilterActive &&
                !hiddenFields?.some((field) => field.name === "coAgent") && (
                  <CoAgentFilterSelect
                    coAgents={coAgents}
                    selectedCoAgentIds={effectiveCoAgentIds}
                    onChange={setPageCoAgentIds}
                    label="Filter by Team Members"
                    size="small"
                    fullWidth={false}
                    sx={{
                      minWidth: 170,
                      flex: "0 1 220px",
                      backgroundColor: "white",
                      "& .MuiOutlinedInput-root": {
                        "&:hover fieldset": {
                          borderColor: "primary.main",
                        },
                      },
                    }}
                  />
                )}

              <Tooltip title="Data analyzed">
                <ToggleButtonGroup
                  value={mode}
                  exclusive
                  onChange={(_, newMode) => newMode && setMode(newMode)}
                  size="small"
                  aria-label="Data analyzed"
                  sx={{
                    "& .MuiToggleButton-root": {
                      px: 1.5,
                      py: 0.5,
                      whiteSpace: "nowrap",
                      borderColor: "#dee2e6",
                      color: "#6c757d",
                      "&.Mui-selected": {
                        backgroundColor: "primary.main",
                        color: "primary.contrastText",
                        "&:hover": {
                          backgroundColor: "primary.dark",
                        },
                      },
                      "&:hover": {
                        backgroundColor: "#f8f9fa",
                      },
                    },
                  }}
                >
                  <ToggleButton value="count">Units</ToggleButton>
                  <ToggleButton value="amount">Volume</ToggleButton>

                  <ToggleButton value="average">
                    Average Price (S&amp;B)
                  </ToggleButton>
                </ToggleButtonGroup>
              </Tooltip>

              <Tooltip title="View mode">
                <ToggleButtonGroup
                  value={timePeriod}
                  exclusive
                  onChange={(_, newTimePeriod) =>
                    newTimePeriod && setTimePeriod(newTimePeriod)
                  }
                  size="small"
                  aria-label="View mode"
                  sx={{
                    "& .MuiToggleButton-root": {
                      px: 1.5,
                      py: 0.5,
                      whiteSpace: "nowrap",
                      borderColor: "#dee2e6",
                      color: "#6c757d",
                      "&.Mui-selected": {
                        backgroundColor: "primary.main",
                        color: "primary.contrastText",
                        "&:hover": {
                          backgroundColor: "primary.dark",
                        },
                      },
                      "&:hover": {
                        backgroundColor: "#f8f9fa",
                      },
                    },
                  }}
                >
                  {realSelectedYearCount !== 1 && (
                    <ToggleButton value="year">Yearly</ToggleButton>
                  )}
                  <ToggleButton value="quarter">Quarterly</ToggleButton>
                  <ToggleButton value="month">Monthly</ToggleButton>
                </ToggleButtonGroup>
              </Tooltip>
            </Box>

            {/* Category Tabs */}
            <Box
              sx={{
                pt: 1,
                borderTop: "1px solid #e9ecef",
              }}
            >
              <Tabs
                value={selectedCategory}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  minHeight: 40,
                  "& .MuiTabs-flexContainer": { gap: 1 },
                  "& .MuiTab-root": {
                    fontWeight: 600,
                    textTransform: "none",
                    fontSize: "0.8125rem",
                    minHeight: 34,
                    minWidth: "auto",
                    px: 1.5,
                    py: 0.5,
                    whiteSpace: "nowrap",
                    borderRadius: "8px",
                    color: "#495057",
                    background: "white",
                    border: "1px solid #dee2e6",
                    "&:hover": {
                      backgroundColor: "#f8f9fa",
                      borderColor: "primary.main",
                    },
                    "&.Mui-selected": {
                      backgroundColor: "primary.main",
                      color: "primary.contrastText",
                      border: "1px solid",
                      borderColor: "primary.main",
                      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.15)",
                    },
                  },
                  "& .MuiTabs-indicator": { display: "none" },
                  "& .MuiTabs-scrollButtons": {
                    color: "primary.dark",
                    "&.Mui-disabled": {
                      opacity: 0.3,
                    },
                  },
                }}
              >
                {visibleCategoryColumns.map((category) => (
                  <Tab
                    label={categoryLabels[category]}
                    value={category}
                    key={category}
                    disableRipple
                  />
                ))}
              </Tabs>
            </Box>
          </Box>
        </>
      )}

      {isMobile && (
        <>
          <Fab
            color="primary"
            sx={{
              position: "fixed",
              bottom: 24,
              left: 24,
              zIndex: 1300,
              boxShadow: "0 6px 20px rgba(25, 118, 210, 0.4)",
              "&:hover": {
                boxShadow: "0 8px 25px rgba(25, 118, 210, 0.5)",
                transform: "scale(1.1)",
              },
              transition: "all 0.3s ease",
            }}
            onClick={() => setMobileFiltersOpen(true)}
          >
            <Badge
              color="secondary"
              variant="dot"
              invisible={selectedYearIds.length === 0 && mode === "count"}
            >
              <FilterListIcon />
            </Badge>
          </Fab>

          <Drawer
            anchor="left"
            open={mobileFiltersOpen}
            onClose={() => setMobileFiltersOpen(false)}
            sx={{
              zIndex: 1400,
              "& .MuiDrawer-paper": {
                width: { xs: 320, sm: 360 },
                backgroundColor: "#f8f9fa",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: 2,
                borderBottom: "1px solid #e0e0e0",
              }}
            >
              <Typography variant="h6" color="primary">
                Filters
              </Typography>
              <IconButton onClick={() => setMobileFiltersOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
            {FilterContent}
          </Drawer>
        </>
      )}

      {/* Main Content Area */}
      <Box sx={{ p: { xs: 2, md: 1 } }}>
        {isMobile && (
          <Typography
            variant="h4"
            color="primary"
            component="h1"
            fontWeight="bold"
            gutterBottom
            sx={{ mb: 4 }}
          >
            Analytics Overview
          </Typography>
        )}

        <Box>
          <Grid container spacing={3}>
            {!["total", "average", "leadToClosed"].includes(selectedCategory) && (
              <Grid item xs={12} md={12} lg={6}>
                <Box
                  sx={{
                    p: 3,
                    backgroundColor: "white",
                    borderRadius: 2,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    border: "1px solid #e0e0e0",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,

                        color: "primary.main",
                      }}
                    >
                      Distribution Overview
                    </Typography>
                    {/* <IconButton
                      onClick={() => setExpandPieChart(true)}
                      size="small"
                      sx={{
                        backgroundColor: "primary.main",
                        color: "white",
                        "&:hover": {
                          backgroundColor: "primary.dark",
                        },
                        px: 1,
                        py: 0.5,
                      }}
                    >
                      <FullscreenIcon sx={{ fontSize: 14 }} />
                    </IconButton> */}
                  </Box>
                  {selectedCategory === "vendors" ? (
                    <BarChartWrapper
                      category={selectedCategory}
                      yearIds={selectedYearIds}
                      includeUnderContracts={includeUnderContracts}
                      mode={mode}
                      onSliceClick={handleSliceClick}
                      yearText={selectedYearText}
                    />
                  ) : (
                    <PieChartWrapper
                      category={
                        selectedCategory === "total" //|| selectedCategory === "average"
                          ? "type"
                          : (selectedCategory as Exclude<typeof selectedCategory, "leadToClosed">)
                      }
                      yearIds={selectedYearIds}
                      includeUnderContracts={includeUnderContracts}
                      mode={mode}
                      onSliceClick={handleSliceClick}
                      yearText={selectedYearText}
                    />
                  )}
                </Box>
              </Grid>
            )}

            {!["total", "average", "leadToClosed"].includes(selectedCategory) && (
              <Grid item xs={12} md={12} lg={6}>
                {selectedVariable ? (
                  <Box
                    sx={{
                      p: 3,
                      backgroundColor: "white",
                      borderRadius: 2,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      border: "1px solid #e0e0e0",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 3,
                        pb: 1,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: "primary.main",
                        }}
                      >
                        Trend Analysis for {selectedVariable.value}
                      </Typography>
                      <IconButton
                        onClick={() => setExpandLineChart(true)}
                        size="small"
                        aria-label="Expand"
                        sx={{
                          // backgroundColor: "gray",
                          // color: "white",
                          // "&:hover": {
                          //   backgroundColor: "primary.dark",
                          // },
                          scale: 1.5,
                          px: 1,
                          py: 0.5,
                        }}
                      >
                        <FullscreenIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                    <LineChartWrapper
                      category={selectedCategory}
                      variableId={selectedVariable.id}
                      selectedYearIds={selectedYearIds}
                      mode={mode}
                      color={selectedVariable.color}
                      yearText={selectedYearText}
                      timePeriod={timePeriod}
                      includeUnderContracts={includeUnderContracts}
                      expandTrigger={expandLineChart}
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      p: 3,
                      backgroundColor: "white",
                      borderRadius: 2,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      border: "1px solid #e0e0e0",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 3,
                        pb: 1,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: "#333",
                        }}
                      >
                        Trend Analysis for All Variables
                      </Typography>
                      <IconButton
                        onClick={() => setExpandAllVariablesChart(true)}
                        size="small"
                        aria-label="Expand"
                        sx={{
                          //color: "white",
                          "&:hover": {
                            //backgroundColor: "primary.dark",
                          },
                          px: 1,
                          py: 0.5,
                          scale: 1.5,
                        }}
                      >
                        <FullscreenIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                    <AllVariablesTrendChart
                      category={selectedCategory}
                      selectedYearIds={selectedYearIds}
                      mode={mode}
                      timePeriod={timePeriod}
                      includeUnderContracts={includeUnderContracts}
                      expandTrigger={expandAllVariablesChart}
                    />
                  </Box>
                )}
              </Grid>
            )}

            {["total", "average"].includes(selectedCategory) && (
              <Grid item xs={12}>
                <Box
                  sx={{
                    p: 3,
                    backgroundColor: "white",
                    borderRadius: 2,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    border: "1px solid #e0e0e0",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 2,
                      fontWeight: 600,
                      color: "#333",
                    }}
                  >
                    {selectedCategory === "total"
                      ? "Total Trend"
                      : "Average Trend"}
                  </Typography>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    This analysis <b>always</b> accounts for Seller and Buyer
                    transactions. Check the Type tab for other transaction
                    types.
                  </Alert>
                  <AggregateTrendChart
                    aggregationType={
                      mode === "average" //|| selectedCategory === "average"
                        ? AggregationTypesEnum.Average
                        : AggregationTypesEnum.Total
                    }
                    mode={mode}
                    selectedYearIds={selectedYearIds}
                    timePeriod={timePeriod}
                    includeUnderContracts={includeUnderContracts}
                    selectedTypeIds={[]}
                    coAgentIds={effectiveCoAgentIds}
                  />
                </Box>
              </Grid>
            )}

            {selectedCategory === "leadToClosed" && (
              <Grid item xs={12}>
                <Box
                  sx={{
                    p: 3,
                    backgroundColor: "white",
                    borderRadius: 2,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    border: "1px solid #e0e0e0",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ mb: 2, fontWeight: 600, color: "#333" }}
                  >
                    Lead to Closed
                  </Typography>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Months from the Lead Received Date to the Closing Date.
                    Only closed Transactions are included; Pipeline entries
                    have not closed yet.
                  </Alert>
                  <LeadToClosedChart
                    yearIds={leadToClosedYearIds}
                    transactionTypeIds={selectedTransactionTypeIds}
                    coAgentIds={effectiveCoAgentIds}
                  />
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>
      </Box>
    </PageLayout>
  );
};

export default Reports;
